/**
 * The ONE seam for the agent platform's Feathers CUSTOM methods.
 *
 * `@intrasync/ai-agent-api` registers eight methods that are not CRUD —
 * `start`/`cancel` on runs, `approve`/`reject` on proposed actions, `report`
 * on usage, `query` on retrieval, `process`/`retry` on ingestion jobs — and its
 * README is explicit that the HOST must register them on the client. HQ's
 * `apiClient` registers nothing explicitly (every service is created on demand
 * with CRUD only), so a socket client does not get them for free.
 *
 * runActions.ts solved this for two of the eight, and its reasoning holds for
 * all of them: build a SEPARATE transport-level handle off the same
 * authenticated socket, call `.methods(...)` on it — the call the
 * socketio-client mixin would have made — and leave the app's service registry
 * untouched so every ServiceStore built at boot keeps working.
 *
 * WIRE SHAPE (verified in runActions.ts against @feathersjs/transport-commons):
 * the package's custom methods take `(id, data, params)`, and the id travels
 * as the single DATA argument: `send('approve', id, {}, {})` reaches the
 * service as `approve(id, …)`. `report` is the exception — it has no id and
 * takes its arguments as data.
 *
 * Returns null, rather than throwing, when the client has no socket transport
 * (`mode: 'test'` builds a transportless client). Nothing is sent to find that
 * out — the check is on the handle, not the wire.
 */

interface TransportService {
  methods?: (...names: string[]) => TransportService;
  [method: string]: unknown;
}

interface TransportConnectionLike {
  service?: (name: string) => TransportService;
}

export interface AgentMethodsApp {
  get?: (key: string) => unknown;
}

export interface AgentMethodHandle {
  /** Invoke one of the methods the handle was built for. */
  call(method: string, ...args: unknown[]): Promise<unknown>;
}

/**
 * A handle over the named custom methods of one agent service, or null when
 * this client cannot carry them.
 */
export const getAgentMethods = (
  app: AgentMethodsApp | null | undefined,
  path: string,
  ...methods: string[]
): AgentMethodHandle | null => {
  const connection = app?.get?.("connection") as TransportConnectionLike | undefined;
  if (!connection || typeof connection.service !== "function") return null;

  const service = connection.service(path);
  if (!service || typeof service.methods !== "function") return null;
  service.methods(...methods);
  for (const m of methods) {
    if (typeof service[m] !== "function") return null;
  }

  return {
    call: (method, ...args) => {
      const fn = service[method];
      if (typeof fn !== "function") {
        return Promise.reject(new Error(`ai-agent: '${method}' is not attached on ${path}`));
      }
      return (fn as (...a: unknown[]) => Promise<unknown>).call(service, ...args);
    },
  };
};
